/**
 * MergeUndoBanner — sticky banner shown for 60s after a merge.
 * Offers an "Ongedaan maken" button and auto-dismisses when the snapshot expires.
 */

import { useEffect, useState } from 'react';
import { useCalendarStore } from '../../stores/calendar-store';

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return `${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`;
}

export function MergeUndoBanner() {
  const { lastMerge, isMerging, unmergeLastMerge, clearLastMerge } = useCalendarStore();
  const [now, setNow] = useState(() => Date.now());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lastMerge) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [lastMerge]);

  useEffect(() => {
    if (!lastMerge) return;
    if (now >= lastMerge.expiresAt) {
      clearLastMerge();
    }
  }, [now, lastMerge, clearLastMerge]);

  if (!lastMerge) return null;

  const remainingMs = lastMerge.expiresAt - now;

  const handleUndo = async () => {
    setError(null);
    try {
      await unmergeLastMerge();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ongedaan maken niet meer mogelijk');
    }
  };

  return (
    <div className="border-b border-emerald-200 bg-emerald-50 text-emerald-900">
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3 text-sm">
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-emerald-600 flex-shrink-0">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>

        <div className="flex-1 min-w-0">
          {error ? (
            <span className="text-red-700">{error}</span>
          ) : (
            <>
              <strong>{lastMerge.sourceName}</strong> samengevoegd met{' '}
              <strong>{lastMerge.targetName}</strong>
              <span className="mx-1.5 text-emerald-500">·</span>
              {lastMerge.movedStopCount} stops verplaatst
            </>
          )}
        </div>

        <span
          className="text-xs text-emerald-700 tabular-nums flex-shrink-0"
          title="Tijd om ongedaan te maken"
        >
          {formatCountdown(remainingMs)}
        </span>

        {!error && (
          <button
            type="button"
            onClick={handleUndo}
            disabled={isMerging}
            className="text-sm font-medium text-emerald-700 hover:text-emerald-900 disabled:opacity-50 flex-shrink-0"
          >
            {isMerging ? 'Bezig…' : 'Ongedaan maken'}
          </button>
        )}

        <button
          type="button"
          onClick={clearLastMerge}
          aria-label="Sluiten"
          className="text-emerald-600 hover:text-emerald-900 flex-shrink-0"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
